import React, { memo, FC, useCallback, useState, useEffect } from 'react';
import { Button, Grid, TextField } from '@material-ui/core';
import { FormikErrors, FormikTouched, FormikValues } from 'formik';
import { Autocomplete } from '@material-ui/lab';
import { useStyles } from '../../styles';
import { emptyLinkType, Language, ValueLabel } from '../../../../../models';
import { Country, LinkType, MasterCode, PlatformForLink } from '../../../RegistrationLinksModel';
import useGetTranslatedOptions from '../useGetTranslatedOptions';
import { FieldLabel, InputField, SelectField } from '../../../../../components';
import { useAppData } from '../../../../../providers';

interface CreateClientLinkTabProps {
  translation: any;
  languages: Language[];
  countries: Country[];
  tradingAccountTypes: PlatformForLink[];
  masterCodes: MasterCode[];
  hasCallCenter: boolean;
  values: any;
  setFieldValue?: any;
  touched?: FormikTouched<FormikValues>;
  errors?: FormikErrors<FormikValues>;
}

export const CreateClientLinkTab: FC<CreateClientLinkTabProps> = memo(({ languages, countries, translation, tradingAccountTypes, masterCodes, hasCallCenter, values, setFieldValue, touched, errors }) => {
  const { userData } = useAppData();
  const [countryOptions, setCountryOptions] = useState<ValueLabel[]>([]);
  const [languageOptions, setLanguageOptions] = useState<ValueLabel[]>([]);
  const [platformOptions, setPlatformOptions] = useState<ValueLabel[]>([]);
  const [linkTypeOptions, setLinkTypeOptions] = useState<ValueLabel[]>([]);
  const [masterCodeOptions, setMasterCodeOptions] = useState<ValueLabel[]>([]);
  const [selectedLinkTypeOption, setSelectedLinkTypeOption] = useState<ValueLabel>();
  const getOptions = useGetTranslatedOptions(translation);
  const [currencyOptions, setCurrencyOptions] = useState<ValueLabel[]>([]);
  const classes = useStyles();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (window.innerWidth <= 540) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }

    window.addEventListener('resize', () => {
      if (window.innerWidth <= 540) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    });
  }, []);

  useEffect(() => {
    console.log(values);
  }, [values]);

  useEffect(() => {
    const options = getOptions(countries, 'name', 'countryId');
    setCountryOptions(options);
    if (options.length === 1) {
      setFieldValue('countryId', options[0].value);
    }

    setPlatformOptions([{ value: '-1', label: translation.text_1668 }, ...getOptions(tradingAccountTypes ?? [], 'name', 'directCode')]);

    const langOptions = getOptions(languages ?? [], 'displayName', 'code');
    setLanguageOptions(langOptions);
    const defaultLang = langOptions.find((lang) => lang.value === 'en');
    if (defaultLang) {
      setFieldValue('language', defaultLang.value);
    }
  }, [getOptions, tradingAccountTypes, translation.text_1668, countries, languages, setFieldValue]);

  const updateCurrencyOptions = useCallback(
    (linkType, values) => {
      let selectedLinkType: LinkType;

      if (values.platformTradingAccountType === '-1') {
        // Should present distinct currencies - on selected linkType
        const linkTypes = tradingAccountTypes?.flatMap((platform) => platform.linkTypes) ?? [];

        const commonCurrencies = linkTypes.flatMap((e) => e.currencies).filter((currency, index, array) => array.indexOf(currency) === index && linkTypes.every((e) => e.currencies.includes(currency)));
        const selectLinkType = linkTypes.find((x) => x.linkType === linkType) ?? emptyLinkType();

        selectedLinkType = commonCurrencies.length > 0 ? { ...selectLinkType, currencies: commonCurrencies } : emptyLinkType();
      } else {
        // Get currencies of selected platform-linkType combo
        const platform = tradingAccountTypes?.find((x) => x.directCode === values.platformTradingAccountType);
        selectedLinkType = platform?.linkTypes?.find((x) => x.linkType === linkType) ?? emptyLinkType();
      }
      const currencies =
        selectedLinkType?.currencies?.map((currencyName) => ({
          value: currencyName,
          label: currencyName,
        })) ?? [];
      setCurrencyOptions([{ value: '-1', label: translation.text_1667 }, ...currencies]);
    },
    [tradingAccountTypes, translation],
  );

  const handleLinkTypeClick = useCallback(
    (option, values, setFieldValue) => {
      values.linkType = option.value;
      setSelectedLinkTypeOption(option);
      setFieldValue('linkType', option.value);
      updateCurrencyOptions(option.value, values);
      setFieldValue('currency', '');
    },
    [updateCurrencyOptions],
  );

  const filterMasterCodes = useCallback(
    (values) => {
      let codes = masterCodes;

      if (values.platformTradingAccountType !== '-1') {
        const accountType = tradingAccountTypes.find((t) => t.directCode === values.platformTradingAccountType);
        codes = codes.filter((m) => m.accountTypeId === accountType?.id);
      }

      if (values.currency !== '-1' && values.currency !== '') {
        codes = codes.filter((m) => m.currency === values.currency);
      }

      setMasterCodeOptions(getOptions(codes, 'masterCode', 'masterCode'));
    },
    [masterCodes, tradingAccountTypes, getOptions],
  );

  const handlePlatformChange = useCallback(
    (e, setFieldValue, values) => {
      const platformDirectCode = e.target.value;
      let options = [];
      if (platformDirectCode === '-1') {
        // Should present distinct link types
        const linkTypes = tradingAccountTypes?.flatMap((platform) => platform.linkTypes) ?? [];
        const typeOptions = getOptions(linkTypes, 'linkTypeText', 'linkType');
        options = typeOptions.filter((option, index, self) => index === self.findIndex((t) => t.value === option.value && t.label === option.label));
      } else {
        const platform = tradingAccountTypes?.find((x) => x.directCode === platformDirectCode);
        options = getOptions(platform?.linkTypes ?? [], 'linkTypeText', 'linkType');
      }
      options.reverse();
      setLinkTypeOptions(options);
      if (options.length > 0) {
        setSelectedLinkTypeOption(options[0]);
        values.linkType = options[0].value;
        values.platformTradingAccountType = platformDirectCode;
        updateCurrencyOptions(options[0].value, values);
        setFieldValue('linkType', options[0].value);
      }
      setFieldValue('platformTradingAccountType', platformDirectCode);
      setFieldValue('currency', '');

      if (values.linkType !== '') updateCurrencyOptions(values.linkType, values);

      filterMasterCodes(values);
    },
    [tradingAccountTypes, updateCurrencyOptions, filterMasterCodes, getOptions],
  );

  const handleCurrencyChange = useCallback(
    (e, setFieldValue, values) => {
      const currency = e.target.value;
      values.currency = currency;
      setFieldValue('currency', currency);

      filterMasterCodes(values);
    },
    [filterMasterCodes],
  );

  const handleMasterCodeChange = useCallback(
    (code, setFieldValue, values) => {
      const masterCode = masterCodes.find((m) => m.masterCode === code);
      let accountType: PlatformForLink | undefined;

      if (masterCode) {
        if (values.platformTradingAccountType === '-1' && (values.currency === '-1' || values.currency === '')) {
          accountType = tradingAccountTypes.find((t) => t.id === masterCode.accountTypeId);
          if (accountType) {
            setFieldValue('platformTradingAccountType', accountType.directCode);
            values.platformTradingAccountType = accountType.directCode;
          }
          setFieldValue('currency', masterCode.currency);
          values.currency = masterCode.currency;
          setFieldValue('masterCode', code);
        } else if (values.platformTradingAccountType === '-1' && values.currency === masterCode.currency) {
          accountType = tradingAccountTypes.find((t) => t.id === masterCode.accountTypeId);
          if (accountType) {
            setFieldValue('platformTradingAccountType', accountType.directCode);
            values.platformTradingAccountType = accountType.directCode;
          }
          setFieldValue('masterCode', code);
        } else {
          accountType = tradingAccountTypes.find((t) => t.directCode === values.platformTradingAccountType);
          if (accountType) {
            if (accountType.id === masterCode.accountTypeId && (values.currency === '-1' || values.currency === '')) {
              setFieldValue('currency', masterCode.currency);
              values.currency = masterCode.currency;
              setFieldValue('masterCode', code);
            }
            if (accountType.id === masterCode.accountTypeId && values.currency === masterCode.currency) {
              setFieldValue('masterCode', code);
            }
          }
        }
        if (accountType) {
          setPlatformOptions(platformOptions.filter((option) => option.value === accountType?.directCode));
          const linkType = selectedLinkTypeOption ? accountType.linkTypes.find((l: any) => l.linkType === selectedLinkTypeOption.value) : accountType.linkTypes[0];
          const filteredCurrencies = linkType?.currencies.filter((c: string) => c === values.currency);
          const currencies =
            filteredCurrencies?.map((currencyName: string) => ({
              value: currencyName,
              label: currencyName,
            })) ?? [];
          setCurrencyOptions(currencies);
        }

        filterMasterCodes(values);
      }
    },
    [tradingAccountTypes, masterCodes, filterMasterCodes, selectedLinkTypeOption, platformOptions],
  );

  const filterOptions = (options: any) => (options.length > 0 ? [...options] : []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <InputField name="linkName" label={translation.link_name} />
      </Grid>
      <Grid item xs={6}>
        <Grid className={classes.fieldContainerLeft}>
          <SelectField
            name="platformTradingAccountType"
            label={translation.text_6247}
            placeholder={translation.text_6248}
            options={platformOptions}
            errors={touched?.platformTradingAccountType ? errors?.platformTradingAccountType : undefined}
            onChange={(e) => handlePlatformChange(e, setFieldValue, values)}
            isRequired
            // icon={
            //   <LightTooltip title={translation.registrationLinksDialog_TATooltip}>
            //     <InfoOutlined className={classes.input} />
            //   </LightTooltip>
            // }
          />
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Grid className={classes.fieldContainerRight}>
          <SelectField
            name="countryId"
            label={translation.text_6244}
            placeholder={translation.text_6245}
            options={countryOptions}
            errors={touched?.countryId ? errors?.countryId : undefined}
            isRequired
            // icon={
            //   <LightTooltip title={translation.registrationLinksDialog_CountryTooltip}>
            //     <InfoOutlined className={classes.input} />
            //   </LightTooltip>
            // }
          />
        </Grid>
      </Grid>
      <Grid item xs={6} className={classes.mobileView}>
        <div className={classes.fieldContainerLeft}>
          <FieldLabel label={translation.text_6243} isRequired />
          {linkTypeOptions.length > 0 ? (
            <Grid container direction="row" className={classes.realDemoButton}>
              {linkTypeOptions.map((option) => (
                <Grid item xs={6}>
                  <Button key={option.value} className={`${classes.linkType} ${option.value === values.linkType ? classes.linkTypeSelected : ''}`} onClick={() => handleLinkTypeClick(option, values, setFieldValue)}>
                    {option.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container direction="row" className={classes.realDemoButton}>
              <Grid item xs={6}>
                <Button key="real" disabled className={`${classes.linkType} ${classes.linkTypeSelected}`}>
                  {translation.real}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button key="demo" disabled className={classes.linkType}>
                  {translation.demo}
                </Button>
              </Grid>
            </Grid>
          )}
        </div>
      </Grid>
      <Grid item xs={6}>
        <Grid className={isMobile ? classes.fieldContainerLeft : classes.fieldContainerRight}>
          <SelectField
            name="currency"
            placeholder={translation.text_6246}
            label={translation.text_6148}
            errors={touched?.currency ? errors?.currency : undefined}
            options={currencyOptions}
            onChange={(e) => handleCurrencyChange(e, setFieldValue, values)}
            disabled={values.platformTradingAccountType === ''}
            isRequired
          />
        </Grid>
      </Grid>
      <Grid item xs={4}>
        <div className={isMobile ? classes.fieldContainerRight : classes.fieldContainerLeft}>
          <SelectField name="language" placeholder={translation.text_6249} label={translation.text_6162} options={languageOptions} errors={touched?.language ? errors?.language : undefined} isRequired />
        </div>
      </Grid>
      <Grid item xs={4}>
        <Grid className={classes.shortcodeContainer}>
          <InputField
            name="shortCode"
            label={translation.text_6250}
            // icon={
            //   <LightTooltip title={translation.text_6251}>
            //     <InfoOutlined className={classes.input} />
            //   </LightTooltip>
            // }
            errors={touched?.shortCode ? errors?.shortCode : undefined}
          />
        </Grid>
      </Grid>
      {hasCallCenter && (
        <Grid item xs={4}>
          <div className={classes.fieldContainerRight}>
            <InputField name="salesCode" label={translation.sales_code} />
          </div>
        </Grid>
      )}
      {JSON.parse(userData).brokerId === 5 && (
        <Grid item xs={4}>
          <div className={hasCallCenter ? classes.fieldContainerLeft : classes.fieldContainerRight}>
            <FieldLabel label={translation.master_code}>
              {/* <LightTooltip title={translation.registrationLinksDialog_MasterCodeTooltip}> */}
              {/*   <InfoOutlined className={classes.input} /> */}
              {/* </LightTooltip> */}
            </FieldLabel>
            <Autocomplete
              value={values.masterCode}
              onChange={(event, newValue: any) => {
                if (typeof newValue === 'string') {
                  handleMasterCodeChange(newValue, setFieldValue, values);
                } else if (newValue && newValue.inputValue) {
                  setFieldValue('masterCode', newValue.inputValue);
                } else if (newValue !== null) {
                  handleMasterCodeChange(newValue.value, setFieldValue, values);
                } else {
                  setFieldValue('masterCode', null);
                }
              }}
              filterOptions={(options, params) => {
                const filtered = filterOptions(options);

                if (params.inputValue !== '') {
                  filtered.push({
                    inputValue: params.inputValue,
                    label: `Add "${params.inputValue}"`,
                  });
                }

                return filtered;
              }}
              className={classes.autoComplete}
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              options={masterCodeOptions}
              getOptionLabel={(option: any) => {
                if (typeof option === 'string') {
                  return option;
                }
                if (option.inputValue) {
                  return option.inputValue;
                }
                return option.label;
              }}
              renderOption={(option) => option.label}
              freeSolo
              renderInput={(params) => <TextField {...params} className={classes.autocompleteField} placeholder="Select or write mastercode..." variant="outlined" margin="dense" />}
            />
          </div>
        </Grid>
      )}
    </Grid>
  );
});
